import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pricing from "@/pages/Pricing";
import SampleReport from "@/pages/SampleReport";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import KvkkNotice from "@/pages/legal/KvkkNotice";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import ExplicitConsent from "@/pages/legal/ExplicitConsent";
import Contact from "@/pages/legal/Contact";
import TermsOfServiceEn from "@/pages/legal/en/TermsOfServiceEn";
import PrivacyPolicyEn from "@/pages/legal/en/PrivacyPolicyEn";
import KvkkNoticeEn from "@/pages/legal/en/KvkkNoticeEn";
import CookiePolicyEn from "@/pages/legal/en/CookiePolicyEn";
import ExplicitConsentEn from "@/pages/legal/en/ExplicitConsentEn";
import AIVisibilityNedir from "@/pages/AIVisibilityNedir";
import GeoNedir from "@/pages/GeoNedir";
import NasilCalisir from "@/pages/NasilCalisir";
import AIVisibilityEn from "@/pages/en/AIVisibilityEn";
import GeoEn from "@/pages/en/GeoEn";
import HowItWorksEn from "@/pages/en/HowItWorksEn";
import Dashboard from "@/pages/Dashboard";
import Analyze from "@/pages/Analyze";
import Tasks from "@/pages/Tasks";
import ContentGenerator from "@/pages/ContentGenerator";
import Competitors from "@/pages/Competitors";
import Reports from "@/pages/Reports";
import AIScore from "@/pages/AIScore";
import QuestionTests from "@/pages/QuestionTests";
import MyBusiness from "@/pages/MyBusiness";
import Settings from "@/pages/Settings";
import AppLayout from "@/components/AppLayout";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";

// Admin bundle is lazy-loaded — non-admin visitors never download these chunks,
// keeping landing/login/dashboard fast.
const AdminLayout = lazy(() => import("@/components/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminBusinesses = lazy(() => import("@/pages/admin/AdminBusinesses"));
const AdminBusinessDetail = lazy(() => import("@/pages/admin/AdminBusinessDetail"));
const AdminScans = lazy(() => import("@/pages/admin/AdminScans"));
const AdminScanDetail = lazy(() => import("@/pages/admin/AdminScanDetail"));
const AdminTasks = lazy(() => import("@/pages/admin/AdminTasks"));
const AdminContents = lazy(() => import("@/pages/admin/AdminContents"));
const AdminPurchaseRequests = lazy(() => import("@/pages/admin/AdminPurchaseRequests"));
const AdminContactRequests = lazy(() => import("@/pages/admin/AdminContactRequests"));

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-slate-400">Yönetici paneli yükleniyor...</div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-forbidden">
        <div className="glass-strong rounded-2xl p-8 max-w-md text-center">
          <div className="text-xs uppercase tracking-wider text-red-400 mb-2">Yetkisiz Erişim</div>
          <h2 className="font-display text-2xl font-semibold text-white mb-2">Bu alana erişim yetkiniz yok</h2>
          <p className="text-sm text-slate-400 mb-6">Yönetici paneline yalnızca admin yetkili hesaplar erişebilir.</p>
          <a href="/app" className="neon-btn inline-block rounded-lg px-4 py-2 text-sm text-white">Kullanıcı Paneline Dön</a>
        </div>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
          <ScrollToTop />
          <Toaster theme="dark" position="top-right" />
          <Routes>
            <Route path="/" element={<Landing lang="en" />} />
            <Route path="/tr" element={<Landing lang="tr" />} />
            <Route path="/giris" element={<Login />} />
            <Route path="/kayit" element={<Register />} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-yenile" element={<ResetPassword />} />
            <Route path="/kullanim-kosullari" element={<TermsOfService />} />
            <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
            <Route path="/kvkk-aydinlatma-metni" element={<KvkkNotice />} />
            <Route path="/cerez-politikasi" element={<CookiePolicy />} />
            <Route path="/acik-riza-metni" element={<ExplicitConsent />} />
            <Route path="/en/terms-of-service" element={<TermsOfServiceEn />} />
            <Route path="/en/privacy-policy" element={<PrivacyPolicyEn />} />
            <Route path="/en/kvkk-notice" element={<KvkkNoticeEn />} />
            <Route path="/en/cookie-policy" element={<CookiePolicyEn />} />
            <Route path="/en/explicit-consent" element={<ExplicitConsentEn />} />
            <Route path="/iletisim" element={<Contact />} />
            <Route path="/ai-visibility-nedir" element={<AIVisibilityNedir />} />
            <Route path="/geo-nedir" element={<GeoNedir />} />
            <Route path="/nasil-calisir" element={<NasilCalisir />} />
            <Route path="/en/what-is-ai-visibility" element={<AIVisibilityEn />} />
            <Route path="/en/what-is-geo" element={<GeoEn />} />
            <Route path="/en/how-it-works" element={<HowItWorksEn />} />
            <Route path="/fiyatlandirma" element={<Pricing />} />
            <Route path="/ornek-rapor" element={<SampleReport />} />

            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="ayarlar" element={<Settings />} />
              <Route path="planlar" element={<Pricing />} />
              <Route path="isletme/:id" element={<Dashboard />} />
              <Route path="isletme/:id/bilgiler" element={<MyBusiness />} />
              <Route path="isletme/:id/skor" element={<AIScore />} />
              <Route path="isletme/:id/sorular" element={<QuestionTests />} />
              <Route path="isletme/:id/analiz" element={<Analyze />} />
              <Route path="isletme/:id/gorevler" element={<Tasks />} />
              <Route path="isletme/:id/icerik" element={<ContentGenerator />} />
              <Route path="isletme/:id/rakipler" element={<Competitors />} />
              <Route path="isletme/:id/rapor" element={<Reports />} />
            </Route>

            <Route path="/admin" element={<AdminProtected><Suspense fallback={<AdminFallback />}><AdminLayout /></Suspense></AdminProtected>}>
              <Route index element={<AdminOverview />} />
              <Route path="kullanicilar" element={<AdminUsers />} />
              <Route path="isletmeler" element={<AdminBusinesses />} />
              <Route path="isletmeler/:id" element={<AdminBusinessDetail />} />
              <Route path="analizler" element={<AdminScans />} />
              <Route path="analizler/:id" element={<AdminScanDetail />} />
              <Route path="gorevler" element={<AdminTasks />} />
              <Route path="icerikler" element={<AdminContents />} />
              <Route path="plan-talepleri" element={<AdminPurchaseRequests />} />
              <Route path="iletisim-talepleri" element={<AdminContactRequests />} />
            </Route>
          </Routes>
          <CookieConsent />
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
